'use client'

import React, {useState, useEffect, useRef} from 'react'
import classes from './BlogTOC.module.css'
import Image from 'next/image'
import minusImg from 'v1/assets/icons/footer/minus-icon.svg'
import plusImg from 'v1/assets/icons/footer/plus-icon.svg'

interface TOCEntry {
  id: string
  title: string
  level: number
  position: number
  children: TOCEntry[]
}

interface FlatTOCEntry {
  id: string
  title: string
  level: number
  position: number
}

interface BlogTOCProps {
  content: string
  title?: string
}

const BlogTOC: React.FC<BlogTOCProps> = ({content, title = 'IN THIS ARTICLE:'}) => {
  const [tocEntries, setTocEntries] = useState<TOCEntry[]>([])
  const [flatEntries, setFlatEntries] = useState<FlatTOCEntry[]>([])
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(true)
  const blogContentRef = useRef<HTMLElement | null>(null)
  const initialRenderRef = useRef<boolean>(true)

  useEffect(() => {
    // Store a reference to the blog content element
    blogContentRef.current = document.getElementById('blog-content')

    // Set a flag to track if this is the initial render
    initialRenderRef.current = true

    // Clean up function
    return () => {
      initialRenderRef.current = false
    }
  }, [])

  // Extract headings and organize hierarchy
  useEffect(() => {
    // Helper function to extract headings with position info
    const extractHeadingsByTag = (regex: RegExp, level: number) => {
      const matches: FlatTOCEntry[] = []
      let match
      while ((match = regex.exec(content)) !== null) {
        const title = match[1].replace(/<[^>]*>/g, '').trim()
        if (title) {
          matches.push({
            id: title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            title,
            level,
            position: match.index,
          })
        }
      }
      return matches
    }

    // Extract all heading types
    const h1Matches = extractHeadingsByTag(/<h1[^>]*>(.*?)<\/h1>/gi, 1)
    const h2Matches = extractHeadingsByTag(/<h2[^>]*>(.*?)<\/h2>/gi, 2)
    const h3Matches = extractHeadingsByTag(/<h3[^>]*>(.*?)<\/h3>/gi, 3)
    const h4Matches = extractHeadingsByTag(/<h4[^>]*>(.*?)<\/h4>/gi, 4)

    // Combine and sort by position
    let headingMatches = [...h1Matches, ...h2Matches, ...h3Matches, ...h4Matches]
    headingMatches.sort((a, b) => a.position - b.position)

    // Add Introduction if missing
    if (!headingMatches.some(entry => entry.title.toLowerCase().includes('introduction'))) {
      headingMatches.unshift({
        id: 'introduction',
        title: 'Introduction',
        level: 1,
        position: 0,
      })
    }

    // Set flat entries for intersection observer
    setFlatEntries(headingMatches)

    // Organize into hierarchy
    const organizeHierarchy = (entries: FlatTOCEntry[]): TOCEntry[] => {
      const result: TOCEntry[] = []
      const currentLevelEntries: {[key: number]: TOCEntry} = {}

      entries.forEach(entry => {
        const newEntry: TOCEntry = {
          ...entry,
          children: [],
        }

        // Find the appropriate parent level
        let parentLevel = entry.level - 1
        while (parentLevel > 0) {
          if (currentLevelEntries[parentLevel]) {
            currentLevelEntries[parentLevel].children.push(newEntry)
            break
          }
          parentLevel--
        }

        // If no parent found, add to root
        if (parentLevel === 0) {
          result.push(newEntry)
        }

        // Update current level entry
        currentLevelEntries[entry.level] = newEntry

        // Clear lower levels when a new heading of this level is encountered
        for (let i = entry.level + 1; i <= 4; i++) {
          delete currentLevelEntries[i]
        }
      })

      return result
    }

    // Set hierarchical entries for rendering
    setTocEntries(organizeHierarchy(headingMatches))

    // Add IDs to headings in the DOM
    const addIdsToHeadings = () => {
      setTimeout(() => {
        const blogContentElement = document.getElementById('blog-content')
        if (!blogContentElement) return

        // Process all headings
        const headings = blogContentElement.querySelectorAll('h1, h2, h3, h4, h5, h6')

        // Match headings with our entries
        headingMatches.forEach(entry => {
          let found = false

          headings.forEach(heading => {
            const headingText = heading.textContent?.trim() || ''
            // Match with or without numbering
            if (
              headingText === entry.title ||
              headingText.replace(/^\d+\.\s+/, '') === entry.title.replace(/^\d+\.\s+/, '')
            ) {
              heading.id = entry.id
              found = true
            }
          })

          // If not found in headings, check other elements
          if (!found) {
            const allElements = blogContentElement.querySelectorAll('p, strong, span')
            allElements.forEach(element => {
              const elementText = element.textContent?.trim() || ''
              if (
                elementText === entry.title ||
                elementText.replace(/^\d+\.\s+/, '') === entry.title.replace(/^\d+\.\s+/, '')
              ) {
                element.id = entry.id
              }
            })
          }
        })

        // Add Introduction ID
        if (!document.getElementById('introduction')) {
          const firstParagraph = blogContentElement.querySelector('p')
          if (firstParagraph && firstParagraph.parentNode) {
            const introDiv = document.createElement('div')
            introDiv.id = 'introduction'
            firstParagraph.parentNode.insertBefore(introDiv, firstParagraph)
          }
        }
      }, 500)
    }

    addIdsToHeadings()
  }, [content])

  // Set up intersection observer for active section tracking
  useEffect(() => {
    if (typeof window !== 'undefined' && flatEntries.length > 0) {
      const blogContentElement = document.getElementById('blog-content')
      if (!blogContentElement) return

      // Create observer for the blog content container
      const observer = new IntersectionObserver(
        entries => {
          // Get all currently intersecting entries
          const intersecting = entries.filter(entry => entry.isIntersecting)

          // If we have intersecting entries, find the one that's closest to the top
          if (intersecting.length > 0) {
            // Sort by their vertical position in the viewport
            intersecting.sort((a, b) => {
              const aRect = a.boundingClientRect
              const bRect = b.boundingClientRect
              return aRect.top - bRect.top
            })

            // Set the topmost visible section as active
            setActiveSection(intersecting[0].target.id)
          }
        },
        {
          root: blogContentElement,
          rootMargin: '-5% 0px -75% 0px', // Adjusted root margin for better accuracy
          threshold: [0.1, 0.5], // Multiple thresholds for better detection
        },
      )

      // Observe all elements that have IDs matching our TOC entries
      flatEntries.forEach(entry => {
        const element = document.getElementById(entry.id)
        if (element) observer.observe(element)
      })

      return () => observer.disconnect()
    }
  }, [flatEntries])

  // Keep the working scroll function - Don't change this
  const scrollToSection = (id: string) => {
    // Get the element and the blog content container
    const element = document.getElementById(id)
    const blogContentElement = document.getElementById('blog-content')

    if (!element || !blogContentElement) {
      return
    }

    try {
      // Simple, reliable approach:
      // 1. Directly measure how far the element is from the top of the content
      const elementPosition = element.getBoundingClientRect().top
      const containerPosition = blogContentElement.getBoundingClientRect().top

      // 2. Calculate how far we need to scroll (the relative position)
      const scrollAmount = elementPosition - containerPosition

      // 3. Add the current scroll position
      const currentScroll = blogContentElement.scrollTop

      // 4. Apply a fixed offset to ensure good positioning
      const offsetAdjustment = 20 // Adjust this value as needed

      // 5. Calculate final position
      const finalPosition = currentScroll + scrollAmount - offsetAdjustment

      // 6. Scroll the container
      blogContentElement.scrollTo({
        top: finalPosition,
        behavior: 'smooth',
      })

      // 7. Update active section
      setActiveSection(id)
    } catch (error) {
      console.error('Error scrolling to section:', error)
    }

    // Prevent default browser behavior
    return false
  }

  // Modified rendering function to fix indentation issue only
  const renderTOCEntries = (entries: TOCEntry[], level = 1) => {
    return entries.map(entry => (
      <React.Fragment key={entry.id}>
        <li
          className={`${classes.tocItem} ${
            // Fix: Only indent if it's NOT one of these main sections
            !isMainSection(entry.title) && level > 1 ? classes.tocItemIndented : ''
          } ${activeSection === entry.id ? classes.tocItemActive : ''}`}
        >
          <a
            href={`#${entry.id}`}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              scrollToSection(entry.id)
            }}
            className={classes.tocLink}
          >
            {entry.title}
          </a>
        </li>
        {entry.children.length > 0 && (
          <ul className={classes.tocNestedList}>{renderTOCEntries(entry.children, level + 1)}</ul>
        )}
      </React.Fragment>
    ))
  }

  // Helper function to identify main sections that should not be indented
  const isMainSection = (title: string): boolean => {
    // List of titles that should not be indented
    return (
      title === 'Introduction' ||
      title === 'What is IT standardization?' ||
      title.includes('Benefits of IT standardization') ||
      title.includes('The multi-location challenge') ||
      title.includes('Implement IT standardization')
    )
  }

  // Don't render anything if we don't have TOC entries
  if (tocEntries.length === 0) {
    return null
  }

  return (
    <div className={classes.tocContainer}>
      <div className={classes.tocHeader} onClick={() => setIsOpen(!isOpen)}>
        <h2 className={classes.tocTitle}>{title}</h2>
        <button
          className={classes.tocToggle}
          aria-label={isOpen ? 'Close table of contents' : 'Open table of contents'}
        >
          {isOpen ? (
            <Image src={minusImg} width={18} alt="minus" />
          ) : (
            <Image src={plusImg} width={18} alt="plus" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className={classes.tocContent}>
          <ul className={classes.tocList}>{renderTOCEntries(tocEntries)}</ul>
        </div>
      )}
    </div>
  )
}

export default BlogTOC